import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import HondaLoader from './components/common/HondaLoader';
import Home from './pages/Home';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import SinPermisos from './pages/SinPermisos';
import UserBlocked from './pages/UserBlocked';
import AnalisisCrediticio from './pages/scoring/AnalisisCrediticio';
import AnalisisCrediticioDisponible from './pages/scoring/AnalisisCrediticioDisponible';
import ScoringDashboard from './pages/scoring/scoringdashboard';
import RevisionDocumentacion from './pages/scoring/RevisionDocumentacion';
import BusquedaCasos from './pages/scoring/BusquedaCasos';
import TrackingEventos from './pages/scoring/TrackingEventos';
import EvaluarExpediente from './pages/scoring/EvaluarExpediente';
import VisualizacionExpediente from './pages/scoring/VisualizacionExpediente';
import Casos from './pages/scoring/casos';
import ABMUsuarios from './pages/ABMUsuarios';
import CesionesDashboard from './pages/cesiones/CesionesDashboard';
import IniciarCesion from './pages/cesiones/IniciarCesion';
import ControlCesiones from './pages/cesiones/ControlCesiones';
import AprobacionCesion from './pages/cesiones/AprobacionCesion';
import BusquedaCesiones from './pages/cesiones/BusquedaCesiones';
import TrackingCesiones from './pages/cesiones/TrackingCesiones';
import VisualizacionCesion from './pages/cesiones/VisualizacionCesion';
import RevisionCesion from './pages/cesiones/RevisionCesion';
import BuyerForm from './pages/cesiones/BuyerForm';
import PrendasDashboard from './pages/prendas/PrendasDashboard';
import PrendasDisponibles from './pages/prendas/PrendasDisponibles';
import AgregarPrenda from './pages/prendas/AgregarPrenda';
import ControlPrendas from './pages/prendas/ControlPrendas';
import BusquedaPrendas from './pages/prendas/BusquedaPrendas';
import TrackingPrendas from './pages/prendas/TrackingPrendas';
import VisualizacionPrenda from './pages/prendas/VisualizacionPrenda';
import EvaluarPrenda from './pages/prendas/EvaluarPrenda';
import ProtectedRoute from './components/common/ProtectedRoute';
import {
  isSessionValid,
  setSessionInvalidCallback,
  checkSessionValidity,
  clearSession,
  SESSION_INVALID_REASON,
  onSessionInvalid,
  canAccessAnalisisCrediticio,
  canAccessDetalleCaso,
  canAccessControlCasos,
  canAccessBusquedaCasos,
  canAccessTrackingEventos,
  canAccessCesionesIniciar,
  canAccessCesionesControl,
  canAccessCesionesAprobacion,
  canAccessCesionesBusqueda,
  canAccessCesionesTracking,
  canAccessCesionesRevision,
  canAccessCesionesVisualizacion,
  canAccessPrendasDisponibles,
  canAccessPrendasControl,
  canAccessPrendasEvaluacion,
  canAccessPrendasBusqueda,
  canAccessPrendasTracking,
  canAccessPrendasVisualizacion,
} from './utils/auth';
// Componente wrapper para usar useNavigate dentro de App
const AppContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Configurar callback de sesión inválida
  useEffect(() => {
    setSessionInvalidCallback((reason) => {
      clearSession();
      let title = 'Sesión cerrada';
      let text = 'Su sesión ha finalizado';
      if (reason === SESSION_INVALID_REASON.CONCURRENT_LOGIN) {
        title = 'Sesión cerrada en este dispositivo';
        text = 'Su sesión fue cerrada porque inició sesión en otro dispositivo o navegador. Si no fue usted, cambie su contraseña inmediatamente.';
      } else if (reason === SESSION_INVALID_REASON.EXPIRED) {
        text = 'Su sesión ha expirado por inactividad';
      } else if (reason === SESSION_INVALID_REASON.USER_INACTIVE) {
        title = 'Usuario inactivo';
        text = 'Su cuenta ha sido desactivada. Contacte al administrador para más información.';
      } else if (reason === SESSION_INVALID_REASON.USER_BLOCKED) {
        title = 'Usuario bloqueado';
        text = 'Su cuenta ha sido bloqueada. Contacte al administrador para más información.';
      }
      Swal.fire({
        icon: 'warning',
        title: title,
        text: text,
        confirmButtonColor: '#CC0000',
        allowOutsideClick: false,
      }).then(() => {
        setUser(null);
        setIsAuthenticated(false);
        navigate('/login');
      });
    });
  }, [navigate]);
  // Verificar sesión en cada cambio de ruta/módulo
  useEffect(() => {
    const verifySession = async () => {
      // Solo verificar si hay sesión activa y no estamos en rutas públicas
      if (isSessionValid() && location.pathname !== '/login' && location.pathname !== '/reset-password') {
        const result = await checkSessionValidity();
        if (!result.isValid) {
          console.warn('⚠️ Sesión invalidada - Razón:', result.reason);
          // Llamar al callback configurado con el reason correcto
          onSessionInvalid(result.reason);
        }
      }
    };
    verifySession();
  }, [location.pathname, navigate]);
  useEffect(() => {
    // Verificar si hay una sesión válida al cargar la aplicación
    if (isSessionValid()) {
      const savedSession = sessionStorage.getItem('userSession');
      try {
        const userData = JSON.parse(savedSession);
        setUser(userData);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Error al cargar sesión:', error);
        sessionStorage.removeItem('userSession');
        setIsAuthenticated(false);
      }
    } else {
      // No hay sesión válida, asegurarse de limpiar
      setIsAuthenticated(false);
      setUser(null);
    }
    setLoading(false);
  }, []);
  const handleLogin = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
  };
  const handleLogout = () => {
    sessionStorage.removeItem('userSession');
    setUser(null);
    setIsAuthenticated(false);
    navigate('/login');
  };
  const handleNavigate = (path, params = {}) => {
    let targetPath = path;

    if (path === 'home') targetPath = '/inicio';
    else if (path === 'login') targetPath = '/login';
    else if (path === 'adminAnalisisCrediticio') targetPath = '/analisis-crediticio';
    else if (path === 'analisisCrediticioDisponible') targetPath = '/analisis-crediticio-disponible';
    else if (path === 'scoringDashboard') targetPath = '/scoring-dashboard';
    else if (path === 'analisisDeCasos') targetPath = '/analisis-de-casos';
    else if (path === 'busquedaCasos') targetPath = '/busqueda-casos';
    else if (path === 'trackingEventos') targetPath = '/tracking-eventos';
    else if (path === 'VerificacionCedente') targetPath = '/verificacion-cedente';
    else if (path === 'evaluarExpediente') targetPath = '/evaluar-expediente';
    else if (path === 'visualizacionExpediente') targetPath = '/visualizacion-expediente';
    else if (path === 'casos') targetPath = '/casos';
    else if (path === 'abmUsuarios') targetPath = '/abm-usuarios';
    // Cesiones
    else if (path === 'cesionesDashboard') targetPath = '/cesiones-dashboard';
    else if (path === 'iniciarCesion') targetPath = '/iniciar-cesion';
    else if (path === 'controlCesiones') targetPath = '/control-cesiones';
    else if (path === 'aprobacionCesion') targetPath = '/aprobacion-cesion';
    else if (path === 'busquedaCesiones') targetPath = '/busqueda-cesiones';
    else if (path === 'trackingCesiones') targetPath = '/tracking-cesiones';
    else if (path === 'visualizacionCesion') targetPath = '/visualizacion-cesion';
    else if (path === 'revisionCesion') targetPath = '/revision-cesion';
    // Prendas
    else if (path === 'prendasDashboard') targetPath = '/prendas-dashboard';
    else if (path === 'prendasDisponibles') targetPath = '/prendas-disponibles';
    else if (path === 'agregarPrenda') targetPath = '/agregar-prenda';
    else if (path === 'controlPrendas') targetPath = '/control-prendas';
    else if (path === 'busquedaPrendas') targetPath = '/busqueda-prendas';
    else if (path === 'trackingPrendas') targetPath = '/tracking-prendas';
    else if (path === 'visualizacionPrenda') targetPath = '/visualizacion-prenda';
    else if (path === 'evaluarPrenda') targetPath = '/evaluar-prenda';
    else if (path === 'logout') {
      handleLogout();
      return;
    } else if (path === 'back') {
      navigate(-1);
      return;
    }

    navigate(targetPath, { state: params });
  };
  if (loading) {
    return (
      <HondaLoader
        message="Cargando aplicación..."
        variant="fullscreen"
        size="large"
        subtitle="Verificando sesión"
      />
    );
  }
  return (
    <Routes>
      {/* Rutas públicas - No requieren autenticación */}
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/login" element={<Login onLogin={handleLogin} />} />
      {/* Página de usuario bloqueado */}
      <Route path="/usuario-bloqueado" element={<UserBlocked />} />
      {/* Página de sin permisos */}
      <Route path="/sin-permisos" element={<SinPermisos />} />
      {/* Ruta de inicio - Solo requiere autenticación, sin módulos específicos */}
      <Route
        path="/inicio"
        element={
          <ProtectedRoute>
            <Home user={user} onNavigate={handleNavigate} onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />
      {/* Módulo de Scoring - Requiere módulo SCORING + permisos por grupo */}
      <Route
        path="/analisis-crediticio"
        element={
          <ProtectedRoute module="SCORING">
            <AnalisisCrediticio user={user} onNavigate={handleNavigate} onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />
      {/* Análisis Crediticio Disponible - Solo Concesionario y Xerox */}
      <Route
        path="/analisis-crediticio-disponible"
        element={
          isSessionValid() && canAccessAnalisisCrediticio() ? (
            <ProtectedRoute module="SCORING">
              <AnalisisCrediticioDisponible user={user} onNavigate={handleNavigate} onLogout={handleLogout} />
            </ProtectedRoute>
          ) : (
            <Navigate to="/sin-permisos" replace />
          )
        }
      />
      <Route
        path="/scoring-dashboard"
        element={
          <ProtectedRoute module="SCORING">
            <ScoringDashboard user={user} onNavigate={handleNavigate} onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />
      {/* Análisis de Casos (Revisión Documentación) - Solo Honda y Xerox */}
      <Route
        path="/analisis-de-casos"
        element={
          isSessionValid() && canAccessDetalleCaso() ? (
            <ProtectedRoute module="SCORING">
              <RevisionDocumentacion user={user} onNavigate={handleNavigate} onLogout={handleLogout} />
            </ProtectedRoute>
          ) : (
            <Navigate to="/sin-permisos" replace />
          )
        }
      />
      {/* Búsqueda de Casos - Todos los grupos */}
      <Route
        path="/busqueda-casos"
        element={
          isSessionValid() && canAccessBusquedaCasos() ? (
            <ProtectedRoute module="SCORING">
              <BusquedaCasos user={user} onNavigate={handleNavigate} onLogout={handleLogout} />
            </ProtectedRoute>
          ) : (
            <Navigate to="/sin-permisos" replace />
          )
        }
      />
      {/* Tracking de Eventos - Todos los grupos */}
      <Route
        path="/tracking-eventos"
        element={
          isSessionValid() && canAccessTrackingEventos() ? (
            <ProtectedRoute module="SCORING">
              <TrackingEventos user={user} onNavigate={handleNavigate} onLogout={handleLogout} />
            </ProtectedRoute>
          ) : (
            <Navigate to="/sin-permisos" replace />
          )
        }
      />
      <Route
        path="/verificacion-cedente"
        element={
          <IniciarCesion
            user={user}
            onLogout={handleLogout}
            onNavigate={handleNavigate}
          />
        }
      />
      <Route
        path="/evaluar-expediente"
        element={
          <ProtectedRoute module="SCORING">
            <EvaluarExpediente user={user} onNavigate={handleNavigate} onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/visualizacion-expediente"
        element={
          <ProtectedRoute module="SCORING">
            <VisualizacionExpediente user={user} onNavigate={handleNavigate} onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />
      {/* Control de Casos - Solo Concesionario y Xerox */}
      <Route
        path="/casos"
        element={
          isSessionValid() && canAccessControlCasos() ? (
            <ProtectedRoute module="SCORING">
              <Casos user={user} onNavigate={handleNavigate} onLogout={handleLogout} />
            </ProtectedRoute>
          ) : (
            <Navigate to="/sin-permisos" replace />
          )
        }
      />
      {/* Módulo ABM - Requiere módulo ABM */}
      <Route
        path="/abm-usuarios"
        element={
          <ProtectedRoute module="ABM">
            <ABMUsuarios user={user} onNavigate={handleNavigate} onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />

      {/* ──────────────────────────────────────────────────────────── */}
      {/* MÓDULO CESIONES */}
      {/* ──────────────────────────────────────────────────────────── */}
      <Route
        path="/cesiones-dashboard"
        element={
          <ProtectedRoute module="CESIONES">
            <CesionesDashboard user={user} onNavigate={handleNavigate} onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />
      {/* Iniciar Cesión - Concesionario y Xerox */}
      <Route
        path="/iniciar-cesion"
        element={
          isSessionValid() && canAccessCesionesIniciar() ? (
            <ProtectedRoute module="CESIONES">
              <IniciarCesion user={user} onNavigate={handleNavigate} onLogout={handleLogout} />
            </ProtectedRoute>
          ) : (
            <Navigate to="/sin-permisos" replace />
          )
        }
      />
      {/* Control de Cesiones - Concesionario y Xerox */}
      <Route
        path="/control-cesiones"
        element={
          isSessionValid() && canAccessCesionesControl() ? (
            <ProtectedRoute module="CESIONES">
              <ControlCesiones user={user} onNavigate={handleNavigate} onLogout={handleLogout} />
            </ProtectedRoute>
          ) : (
            <Navigate to="/sin-permisos" replace />
          )
        }
      />
      {/* Aprobación de Cesión - Honda y Xerox */}
      <Route
        path="/aprobacion-cesion"
        element={
          isSessionValid() && canAccessCesionesAprobacion() ? (
            <ProtectedRoute module="CESIONES">
              <AprobacionCesion user={user} onNavigate={handleNavigate} onLogout={handleLogout} />
            </ProtectedRoute>
          ) : (
            <Navigate to="/sin-permisos" replace />
          )
        }
      />
      {/* Búsqueda de Cesiones - Todos */}
      <Route
        path="/busqueda-cesiones"
        element={
          isSessionValid() && canAccessCesionesBusqueda() ? (
            <ProtectedRoute module="CESIONES">
              <BusquedaCesiones user={user} onNavigate={handleNavigate} onLogout={handleLogout} />
            </ProtectedRoute>
          ) : (
            <Navigate to="/sin-permisos" replace />
          )
        }
      />
      {/* Tracking de Cesiones - Todos */}
      <Route
        path="/tracking-cesiones"
        element={
          isSessionValid() && canAccessCesionesTracking() ? (
            <ProtectedRoute module="CESIONES">
              <TrackingCesiones user={user} onNavigate={handleNavigate} onLogout={handleLogout} />
            </ProtectedRoute>
          ) : (
            <Navigate to="/sin-permisos" replace />
          )
        }
      />
      {/* Visualización de Cesión - Todos los roles con CESIONES */}
      <Route
        path="/visualizacion-cesion"
        element={
          isSessionValid() && canAccessCesionesVisualizacion() ? (
            <ProtectedRoute module="CESIONES">
              <VisualizacionCesion user={user} onNavigate={handleNavigate} onLogout={handleLogout} />
            </ProtectedRoute>
          ) : (
            <Navigate to="/sin-permisos" replace />
          )
        }
      />
      {/* Revisión de Cesiones - Honda y Xerox */}
      <Route
        path="/revision-cesion"
        element={
          isSessionValid() && canAccessCesionesRevision() ? (
            <ProtectedRoute module="CESIONES">
              <RevisionCesion user={user} onNavigate={handleNavigate} onLogout={handleLogout} />
            </ProtectedRoute>
          ) : (
            <Navigate to="/sin-permisos" replace />
          )
        }
      />

      {/* Ruta pública para formulario de comprador (sin autenticación) */}
      <Route path="/buyerForm" element={<BuyerForm />} />

      {/* ──────────────────────────────────────────────────────────── */}
      {/* MÓDULO PRENDAS */}
      {/* ──────────────────────────────────────────────────────────── */}
      <Route
        path="/prendas-dashboard"
        element={
          <ProtectedRoute module="PRENDAS">
            <PrendasDashboard user={user} onNavigate={handleNavigate} onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />
      {/* Prendas Disponibles - Concesionario y Xerox */}
      <Route
        path="/prendas-disponibles"
        element={
          isSessionValid() && canAccessPrendasDisponibles() ? (
            <ProtectedRoute module="PRENDAS">
              <PrendasDisponibles user={user} onNavigate={handleNavigate} onLogout={handleLogout} />
            </ProtectedRoute>
          ) : (
            <Navigate to="/sin-permisos" replace />
          )
        }
      />
      {/* Agregar Prenda - Concesionario y Xerox */}
      <Route
        path="/agregar-prenda"
        element={
          isSessionValid() && canAccessPrendasDisponibles() ? (
            <ProtectedRoute module="PRENDAS">
              <AgregarPrenda user={user} onNavigate={handleNavigate} onLogout={handleLogout} />
            </ProtectedRoute>
          ) : (
            <Navigate to="/sin-permisos" replace />
          )
        }
      />
      {/* Control de Prendas - Concesionario y Xerox */}
      <Route
        path="/control-prendas"
        element={
          isSessionValid() && canAccessPrendasControl() ? (
            <ProtectedRoute module="PRENDAS">
              <ControlPrendas user={user} onNavigate={handleNavigate} onLogout={handleLogout} />
            </ProtectedRoute>
          ) : (
            <Navigate to="/sin-permisos" replace />
          )
        }
      />
      {/* Búsqueda de Prendas - Todos */}
      <Route
        path="/busqueda-prendas"
        element={
          isSessionValid() && canAccessPrendasBusqueda() ? (
            <ProtectedRoute module="PRENDAS">
              <BusquedaPrendas user={user} onNavigate={handleNavigate} onLogout={handleLogout} />
            </ProtectedRoute>
          ) : (
            <Navigate to="/sin-permisos" replace />
          )
        }
      />
      {/* Tracking de Prendas - Todos */}
      <Route
        path="/tracking-prendas"
        element={
          isSessionValid() && canAccessPrendasTracking() ? (
            <ProtectedRoute module="PRENDAS">
              <TrackingPrendas user={user} onNavigate={handleNavigate} onLogout={handleLogout} />
            </ProtectedRoute>
          ) : (
            <Navigate to="/sin-permisos" replace />
          )
        }
      />
      {/* Visualización de Prenda - Todos */}
      <Route
        path="/visualizacion-prenda"
        element={
          isSessionValid() && canAccessPrendasVisualizacion() ? (
            <ProtectedRoute module="PRENDAS">
              <VisualizacionPrenda user={user} onNavigate={handleNavigate} onLogout={handleLogout} />
            </ProtectedRoute>
          ) : (
            <Navigate to="/sin-permisos" replace />
          )
        }
      />
      {/* Evaluar Prenda - Honda y Xerox */}
      <Route
        path="/evaluar-prenda"
        element={
          isSessionValid() && canAccessPrendasEvaluacion() ? (
            <ProtectedRoute module="PRENDAS">
              <EvaluarPrenda user={user} onNavigate={handleNavigate} onLogout={handleLogout} />
            </ProtectedRoute>
          ) : (
            <Navigate to="/sin-permisos" replace />
          )
        }
      />

      {/* Rutas por defecto */}
      <Route path="/" element={<Navigate to={isAuthenticated ? "/inicio" : "/login"} replace />} />
      <Route path="*" element={<Navigate to={isAuthenticated ? "/inicio" : "/login"} replace />} />
    </Routes>
  );
};
function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Ruta pública completamente aislada del sistema de autenticación */}
        <Route path="/buyerForm" element={<BuyerForm />} />
        {/* Todo lo demás pasa por AppContent (sesión, auth, etc.) */}
        <Route path="/*" element={<AppContent />} />
      </Routes>
    </BrowserRouter>
  );
}
export default App;
